import { Button, Checkbox } from '@any-ui-react/core';
import { useModals } from '@any-ui-react/modals';
import { useLocalStorage } from '@mantine/hooks';
import classNames from 'classnames';
import {
  type Dispatch,
  forwardRef,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { ModalFooter } from '@/common/components/shared/modal-footer/ModalFooter';
import { Spinner } from '@/common/components/shared/spinner/Spinner';
import { useCurrentEmployeeContext } from '@/common/contexts/CurrentEmployeeContext';
import { Cell } from './Cell';
import { CellValueRequest } from './CellValueRequest';
import { CellValueStatus } from './CellValueStatus';
import { Row } from './Row';

const headerClassNames = 'py-2 border-b bg-gray-0 text-2xs font-semibold leading-13 whitespace-nowrap';

interface Props<R> {
  modalId: string | undefined;
  classNames: {
    gridCols: string;
    gridColSpanWhenEmpty: string;
  };
  columns: {
    secondHeader: (headerClassNames: string) => ReactNode;
    secondData: (row: R) => ReactNode;
    resultData: (row: R) => ReactNode;
  };
  emptyMessage: ReactNode;
  loading: boolean;
  rows: Array<R>;
  refetch: () => void;
  onChangeOnlyMyTask: Dispatch<SetStateAction<boolean>>;
  onClose?: () => void;
}

export interface HistoryListHandle {
  /** Re-runs the active history query - call when a host tab switches to "History". */
  refetch: () => void;
}

/**
 * Shared history-table shell for export and import history lists.
 * Renders a grid of rows with columns for requested-at, format/type, status, and progress/result.
 * Includes a "Only my tasks" checkbox (visible to superadmins) and a close button for modal usage.
 */
export const HistoryList = forwardRef<HistoryListHandle, Props<any>>(
  (
    { modalId, classNames: propClassNames, columns, emptyMessage, loading, rows, refetch, onChangeOnlyMyTask, onClose },
    ref,
  ) => {
    const { closeModal } = useModals();
    const [onlyMyTask, setOnlyMyTask] = useLocalStorage({
      key: 'med|filters|export-import|onlyMyTask',
      defaultValue: true,
    });
    const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
    const { currentEmployee } = useCurrentEmployeeContext();

    useImperativeHandle(ref, () => ({ refetch }), [refetch]);

    useEffect(() => {
      onChangeOnlyMyTask(onlyMyTask);
    }, [onChangeOnlyMyTask, onlyMyTask]);

    return (
      <div className="grid grid-rows-[auto_1fr]">
        <div className="px-4 py-2 text-xs">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              Showing histories in latest 1 week.
              <br />
              Google Sheets links may be unavailable if you delete the file on Google Drive.
            </span>
            {currentEmployee?.accessFlag.isSuperAdmin && (
              <Checkbox
                checked={onlyMyTask}
                label="Only my tasks"
                onChange={event => setOnlyMyTask(event.currentTarget.checked)}
              />
            )}
          </div>
        </div>
        <div className={classNames('grid border-t', propClassNames.gridCols)}>
          <div className={classNames(headerClassNames, 'pl-4')}>Requested at</div>
          {columns.secondHeader(headerClassNames)}
          <div className={classNames(headerClassNames, 'px-2 text-center')}>Status</div>
          <div className={classNames(headerClassNames, 'pr-4')}>Progress / Result</div>
          {loading && rows.length === 0 ? (
            <div className={classNames(propClassNames.gridColSpanWhenEmpty, 'py-10')}>
              <Spinner />
            </div>
          ) : rows.length === 0 ? (
            <div className={classNames(propClassNames.gridColSpanWhenEmpty, 'py-10 text-center text-gray-7 text-sm')}>
              {emptyMessage}
            </div>
          ) : (
            rows.map(row => (
              <Row key={row.id} rowId={row.id} hoveredRowId={hoveredRowId} setHoveredRowId={setHoveredRowId}>
                <CellValueRequest {...row} onlyMyTask={onlyMyTask} />
                {columns.secondData(row)}
                <Cell className="px-2 items-center justify-center">
                  <CellValueStatus status={row.status} />
                </Cell>
                <Cell className="pr-4 items-center justify-start w-full break-all">{columns.resultData(row)}</Cell>
              </Row>
            ))
          )}
        </div>
        {(modalId || onClose) && (
          <ModalFooter spacerSize={1} disableMinusMarginX={true}>
            <Button variant="default" onClick={() => (modalId ? closeModal(modalId) : onClose?.())}>
              Close
            </Button>
          </ModalFooter>
        )}
      </div>
    );
  },
) as <R>(props: Props<R> & { ref: Ref<HistoryListHandle> }) => ReactElement;
